import React, { FC, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';

import {
  AddContributorIcon,
  CloseCircleIconSmall,
  CrossIcon,
  EditIcon,
  GrantAccessIcon,
  RevokeAccessIcon,
  SendInviteIcon,
  TrashBinIcon,
} from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { Checkbox } from '../../../components/checkbox/index';
import { Sheet, SheetContent } from '../../../components/sheet/index';
import { useSelectedCompany } from '../../../hooks/index';
import { Stakeholder, StakeholderType } from '../../../types/stakeholder.types';
import { getStakeholderCommonActions } from '../stakeholder-permissions.const';
import { SelectedStakeholder } from '../stakeholders';

const mergeTrueFields = <T extends { [key: string]: boolean }>(arr: T[]): Partial<T> => {
  let result: Partial<T> = {};

  if (arr.length === 0) {
    return result;
  }

  result = { ...arr[0] };

  Object.keys(result).forEach((key) => {
    const allTrue = arr.every((obj) => obj[key] === true);
    if (!allTrue) {
      delete result[key];
    }
  });

  return result;
};

export type BulkActionProps = {
  className?: string;
  icon: ReactNode;
  onClick: () => void;
  plural?: string;
  title: ReactNode;
};

const BulkAction: FC<BulkActionProps> = ({
  icon,
  onClick,
  title,
  className,
  plural,
  ...attributes
}) => (
  <Button
    {...attributes}
    className={twMerge(
      'flex h-5 gap-2 text-nowrap pl-3 text-sm font-[450] text-gray-700',
      className,
    )}
    onClick={onClick}
    styleType="NONE"
  >
    {icon}
    {title}
    {plural}
  </Button>
);

const useBulkStakeholderActions = (stakeholders: SelectedStakeholder[]) => {
  const data = stakeholders.map((stakeholder) => {
    const possibleActions = getStakeholderCommonActions({ ...stakeholder });
    return possibleActions;
  });

  return mergeTrueFields(data);
};

type ActionProps = { id: string[]; onSuccess: () => void };

export type BulkActionsModalProps = {
  handleCancelInvitation: (props: ActionProps) => void;
  handleDelete: (props: ActionProps) => void;
  handleGrantAccess: (props: ActionProps) => void;
  handleMakeContributor: (props: ActionProps) => void;
  handleOpenEditTerminationModal: (data?: Stakeholder[] | undefined) => void;
  handleOpenTerminateModal: (data?: Stakeholder[] | undefined) => void;
  handleRemoveContributorRole: (props: ActionProps) => void;
  handleResendInvitation: (props: ActionProps) => void;
  handleRevokeAccess: (props: ActionProps) => void;
  handleSendInvitation: (props: ActionProps) => void;
  handleSuccess: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  onRemoveSelectAll: () => void;
  selectedStakeholderIds: string[];
  stakeholders: Stakeholder[];
};

const BulkActionsModal: FC<BulkActionsModalProps> = ({
  isOpenModal,
  selectedStakeholderIds,
  stakeholders,
  handleCancelInvitation,
  handleDelete,
  handleGrantAccess,
  handleMakeContributor,
  handleRemoveContributorRole,
  handleResendInvitation,
  handleRevokeAccess,
  handleSendInvitation,
  handleOpenTerminateModal,
  handleOpenEditTerminationModal,
  handleSuccess,
  onRemoveSelectAll,
  invalidateQuery,
}) => {
  const { selectedCompany } = useSelectedCompany();
  const selectedStakeholders = stakeholders.filter(({ id }) => selectedStakeholderIds.includes(id));
  const isPlural = selectedStakeholderIds.length > 1;
  const accountId = selectedCompany?.stakeholder?.id || '';
  const isAllSelectedStakeholdersTerminated = selectedStakeholders.every(
    (stakeholder) => stakeholder.isTerminated,
  );
  const isSomeSelectedStakeholdersTerminated = selectedStakeholders.some(
    (stakeholder) => stakeholder.isTerminated,
  );

  const {
    canDelete,
    canInvite,
    canTerminate,
    cancelInvitation,
    grantAccess,
    makeContributor,
    resendInvitation,
    revokeAccess,
    revokeContributor,
    canEditTerminate,
  } = useBulkStakeholderActions(selectedStakeholders);

  const hasInvalidStakeholders = selectedStakeholders.some(
    (stakeholder) =>
      (stakeholder.type !== StakeholderType.EMPLOYEE &&
        stakeholder.type !== StakeholderType.FOUNDER &&
        stakeholder.type !== StakeholderType.ADVISOR) ||
      !stakeholder.hasShares,
  );

  const allStakeholdersWithoutShares = selectedStakeholders.every(
    (stakeholder) => !stakeholder.hasShares,
  );

  return (
    <Sheet modal={false} open={isOpenModal}>
      <SheetContent
        className="m-auto flex h-fit w-fit justify-center border-transparent bg-transparent p-0 pb-6 shadow-none"
        side="BOTTOM"
      >
        <div className="flex h-10 items-center justify-center divide-x divide-gray-100 rounded-[40px] bg-white px-4 py-2 shadow-2xl">
          <div
            className="flex h-5 items-center gap-3 text-nowrap pr-3 text-sm font-[450] text-gray-700"
            onClick={onRemoveSelectAll}
          >
            <Checkbox checked={selectedStakeholderIds.length > 0} />
            <AppFormattedMessage
              id="ROWS_SELECTED"
              values={{
                count: selectedStakeholderIds.length,
              }}
            />
          </div>
          {canInvite && (
            <BulkAction
              data-permission-required
              icon={<SendInviteIcon />}
              onClick={() => {
                handleSendInvitation({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success(
                      isPlural
                        ? 'Stakeholders successfully invited'
                        : 'Stakeholder successfully invited',
                    );
                    handleSuccess();
                  },
                });
              }}
              title={isPlural ? 'Invite Stakeholders' : 'Invite Stakeholder'}
            />
          )}

          {resendInvitation && (
            <BulkAction
              data-permission-required
              icon={<SendInviteIcon className="size-4" />}
              onClick={() => {
                handleResendInvitation({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success(
                      isPlural
                        ? 'Invitations successfully resent'
                        : 'Invitation successfully resent',
                    );
                    handleSuccess();
                  },
                });
              }}
              title={
                isPlural ? 'Resend Invitations' : <AppFormattedMessage id="RESEND_INVITATION" />
              }
            />
          )}
          {grantAccess && (
            <BulkAction
              data-permission-required
              icon={<GrantAccessIcon className="size-4" />}
              onClick={() => {
                handleGrantAccess({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success('Access successfully granted');
                    handleSuccess();
                  },
                });
                handleSuccess();
              }}
              title={<AppFormattedMessage id="GRANT_ACCESS" />}
            />
          )}

          {makeContributor && (
            <BulkAction
              data-permission-required
              icon={<AddContributorIcon className="size-4" />}
              onClick={() => {
                handleMakeContributor({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success(
                      isPlural
                        ? 'Contributor roles successfully granted'
                        : 'Contributor role successfully granted',
                    );
                    handleSuccess();
                  },
                });
                handleSuccess();
              }}
              title={
                <>
                  <AppFormattedMessage id="MAKE_CONTRIBUTOR" />
                  {isPlural ? 's' : ''}
                </>
              }
            />
          )}
          {canEditTerminate && isAllSelectedStakeholdersTerminated && !hasInvalidStakeholders && (
            <BulkAction
              data-permission-required
              icon={<EditIcon />}
              onClick={() => {
                handleOpenEditTerminationModal(selectedStakeholders);
                handleSuccess();
              }}
              title={<AppFormattedMessage id="EDIT_TERMINATION" />}
            />
          )}
          {cancelInvitation && (
            <BulkAction
              className="text-fireside-500"
              data-permission-required
              icon={<CloseCircleIconSmall className="size-4" iconColor="#F04438" />}
              onClick={() => {
                handleCancelInvitation({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success(
                      isPlural
                        ? 'Invitations successfully cancelled'
                        : 'Invitation successfully cancelled',
                    );
                    handleSuccess();
                  },
                });
                handleSuccess();
              }}
              title={
                <>
                  <AppFormattedMessage id="CANCEL_INVITATION" />
                  {isPlural ? 's' : ''}
                </>
              }
            />
          )}
          {revokeAccess &&
            !selectedStakeholderIds.includes(accountId) &&
            !selectedStakeholders.some((stakeholder) => stakeholder.isAccessRevoked) &&
            !selectedStakeholders.some((stakeholder) => stakeholder.hasPendingSignatures) && (
              <BulkAction
                data-permission-required
                icon={<RevokeAccessIcon className="size-4" />}
                onClick={() => {
                  handleRevokeAccess({
                    id: selectedStakeholderIds,
                    onSuccess: () => {
                      invalidateQuery();
                      toast.success(
                        isPlural
                          ? 'Stakeholders access successfully revoked'
                          : 'Stakeholder access successfully revoked',
                      );
                      handleSuccess();
                    },
                  });
                  handleSuccess();
                }}
                title={<AppFormattedMessage id="REVOKE_ACCESS" />}
              />
            )}
          {revokeContributor && !selectedStakeholderIds.includes(accountId) && (
            <BulkAction
              data-permission-required
              icon={<RevokeAccessIcon className="size-4" />}
              onClick={() => {
                handleRemoveContributorRole({
                  id: selectedStakeholderIds,
                  onSuccess: () => {
                    invalidateQuery();
                    toast.success(
                      isPlural
                        ? 'Contributor roles successfully revoked'
                        : 'Contributor role successfully revoked',
                    );
                    handleSuccess();
                  },
                });
                handleSuccess();
              }}
              plural={'s'}
              title={
                isPlural ? (
                  <AppFormattedMessage id="REVOKE_CONTRIBUTOR_ROLE" />
                ) : (
                  <AppFormattedMessage id="REVOKE_CONTRIBUTOR_ROLE" />
                )
              }
            />
          )}
          {canTerminate && !isSomeSelectedStakeholdersTerminated && !hasInvalidStakeholders && (
            <BulkAction
              className="text-fireside-500"
              data-permission-required
              icon={<CrossIcon className="size-4" iconColor="#F04438" />}
              onClick={() => {
                handleOpenTerminateModal(selectedStakeholders);
                handleSuccess();
              }}
              plural={'s'}
              title={
                isPlural ? (
                  <AppFormattedMessage id="TERMINATE_STAKEHOLDER" />
                ) : (
                  <AppFormattedMessage id="TERMINATE_STAKEHOLDER" />
                )
              }
            />
          )}

          {allStakeholdersWithoutShares &&
            canDelete &&
            !selectedStakeholderIds.includes(accountId) && (
              <BulkAction
                className="text-fireside-500"
                data-permission-required
                icon={<TrashBinIcon className="size-4" iconColor="#F04438" />}
                onClick={() => {
                  handleDelete({
                    id: selectedStakeholderIds,
                    onSuccess: () => {
                      invalidateQuery();
                      toast.success(
                        isPlural ? 'Successfully deleted' : 'Stakeholder successfully deleted',
                      );
                      handleSuccess();
                    },
                  });
                  handleSuccess();
                }}
                title={
                  <>
                    <AppFormattedMessage
                      id="DELETE_STAKEHOLDER"
                      values={{ count: selectedStakeholderIds.length }}
                    />
                  </>
                }
              />
            )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default BulkActionsModal;
